import { useAttachmentViewUrl } from '@commenda/api/commenda-ai/useAttachmentViewUrl';
import { Skeleton } from '@commenda/kakashi';
import type { ReactElement } from 'react';
import {
  AttachmentHoverCard,
  AttachmentHoverCardContent,
  AttachmentHoverCardTrigger,
} from './ai-elements/attachment';
import { formatAttachmentName, getAttachmentKind } from './attachment-kind';

export function AttachmentPreviewHoverCard({
  fileName,
  mimeType,
  fileRef,
  children,
}: {
  fileName: string;
  mimeType: string;
  fileRef: string;
  children: ReactElement;
}) {
  const { kind, label } = getAttachmentKind(mimeType, fileName, fileRef);
  const isImage = kind === 'image';
  const { url, isLoading, error } = useAttachmentViewUrl(fileRef, isImage);

  if (!isImage || (!url && !isLoading && !error)) return children;

  return (
    <AttachmentHoverCard openDelay={150}>
      <AttachmentHoverCardTrigger asChild>
        {children}
      </AttachmentHoverCardTrigger>
      <AttachmentHoverCardContent align="end" side="top">
        <div className="flex w-64 flex-col gap-md">
          <div className="flex h-40 items-center justify-center overflow-hidden rounded-md border-[0.5px] border-border-primary bg-bg-primary">
            {isLoading ? (
              <Skeleton className="size-full rounded-none" />
            ) : error || !url ? (
              <span className="txt-extra-small-regular text-text-tertiary">
                Preview unavailable
              </span>
            ) : (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                alt={fileName}
                src={url}
                className="max-h-full max-w-full object-contain"
              />
            )}
          </div>

          <div className="flex flex-col gap-xxs px-xxs">
            <span className="truncate txt-small-medium text-text-primary">
              {formatAttachmentName(fileName)}
            </span>
            <span className="txt-extra-small-regular text-text-secondary">
              {label}
            </span>
          </div>
        </div>
      </AttachmentHoverCardContent>
    </AttachmentHoverCard>
  );
}
